"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { subscribeToStorage } from "./subscribeToStorage";

type PreviewSide = "front" | "back";
type SeenPreviews = Record<PreviewSide, string[]>;
type PreviewImage = { url: string | null; path: string | null };

export type PreviewNotificationOptions = {
  storageKey: string | null;
  /** Other durable keys for this same account and dress (for example its ticket). */
  storageAliases?: string[];
  identityKey: string;
  images: Record<PreviewSide, PreviewImage>;
  visibleSides: PreviewSide[];
  renderingSide: PreviewSide | null;
  restorationPending?: boolean;
};

const emptySeen: SeenPreviews = { front: [], back: [] };
const serverSnapshot = () => null;

function readSeen(value: string | null): SeenPreviews {
  if (!value) return emptySeen;
  try {
    const parsed = JSON.parse(value);
    if (parsed?.version !== 1) return emptySeen;
    const readSide = (side: PreviewSide): string[] =>
      Array.isArray(parsed[side])
        ? parsed[side].filter(
            (item: unknown): item is string => typeof item === "string"
          )
        : [];
    return { front: readSide("front"), back: readSide("back") };
  } catch {
    return emptySeen;
  }
}

function imageIdentity(image: PreviewImage): string | null {
  // A path alone is not a displayable preview. Prefer it as the identity once
  // the image is ready, so refreshing a signed URL does not make it unread.
  const url = image.url?.trim();
  if (!url) return null;
  const path = image.path?.trim();
  return path ? `path:${path}` : `url:${url}`;
}

function sameSeen(left: SeenPreviews, right: SeenPreviews) {
  return (["front", "back"] as const).every(
    side =>
      left[side].length === right[side].length &&
      left[side].every((identity, index) => identity === right[side][index])
  );
}

/** Presentation memory for completed previews; edits and progress are not notifications. */
export function usePreviewNotifications({
  storageKey,
  storageAliases = [],
  identityKey,
  images,
  visibleSides,
  renderingSide,
  restorationPending = false,
}: PreviewNotificationOptions): Record<PreviewSide, boolean> {
  const [memory, setMemory] = useState<{
    identityKey: string;
    storageKey: string | null;
    seen: SeenPreviews;
  } | null>(null);
  // Callers may pass an inline alias array. Keep subscription/effect identity
  // stable and write each durable account/dress key at most once.
  const serializedKeys = JSON.stringify(
    [...new Set([storageKey, ...storageAliases])].filter((key): key is string =>
      Boolean(key)
    )
  );
  const storageKeys = useMemo<string[]>(
    () => JSON.parse(serializedKeys),
    [serializedKeys]
  );
  const readSnapshot = useCallback(() => {
    if (!storageKeys.length) return null;
    try {
      const snapshots = storageKeys.map(key =>
        readSeen(localStorage.getItem(`designer-preview-seen:${key}`))
      );
      return JSON.stringify({
        version: 1,
        front: [...new Set(snapshots.flatMap(snapshot => snapshot.front))],
        back: [...new Set(snapshots.flatMap(snapshot => snapshot.back))],
      });
    } catch {
      return null;
    }
  }, [storageKeys]);
  const savedValue = useSyncExternalStore(
    subscribeToStorage,
    readSnapshot,
    serverSnapshot
  );
  const savedSeen = useMemo(() => readSeen(savedValue), [savedValue]);
  // Carry acknowledgments into the first durable session key or an explicitly
  // identified same-dress alias, but never into an unrelated account/dress.
  const memorySeen =
    memory?.identityKey === identityKey &&
    (memory.storageKey === storageKey ||
      memory.storageKey === null ||
      storageKeys.includes(memory.storageKey))
      ? memory.seen
      : emptySeen;
  const seen = useMemo(
    () => ({
      front: [...new Set([...savedSeen.front, ...memorySeen.front])],
      back: [...new Set([...savedSeen.back, ...memorySeen.back])],
    }),
    [savedSeen, memorySeen]
  );
  const front =
    !restorationPending && renderingSide !== "front"
      ? imageIdentity(images.front)
      : null;
  const back =
    !restorationPending && renderingSide !== "back"
      ? imageIdentity(images.back)
      : null;
  const frontVisible = visibleSides.includes("front");
  const backVisible = visibleSides.includes("back");

  const nextSeen = useMemo<SeenPreviews>(
    () => ({
      front:
        frontVisible && front && !seen.front.includes(front)
          ? [...seen.front, front]
          : seen.front,
      back:
        backVisible && back && !seen.back.includes(back)
          ? [...seen.back, back]
          : seen.back,
    }),
    [back, backVisible, front, frontVisible, seen]
  );
  const hasSeen = Boolean(nextSeen.front.length || nextSeen.back.length);
  // Record the transition before children render, so moving away from a viewed
  // image cannot briefly restore its badge. The equality guard converges after
  // one render and also scopes transient memory to the durable session key.
  if (
    !restorationPending &&
    hasSeen &&
    (memory?.identityKey !== identityKey ||
      memory.storageKey !== storageKey ||
      !sameSeen(memory.seen, nextSeen))
  ) {
    setMemory({ identityKey, storageKey, seen: nextSeen });
  }

  useEffect(() => {
    if (restorationPending || !hasSeen || !storageKeys.length) return;
    const value = JSON.stringify({ version: 1, ...nextSeen });
    try {
      for (const key of storageKeys) {
        const storageName = `designer-preview-seen:${key}`;
        if (localStorage.getItem(storageName) !== value) {
          localStorage.setItem(storageName, value);
        }
      }
    } catch {
      /* Acknowledgments still work for this mounted session without storage. */
    }
  }, [hasSeen, nextSeen, restorationPending, storageKeys]);

  return {
    front: Boolean(front && !frontVisible && !seen.front.includes(front)),
    back: Boolean(back && !backVisible && !seen.back.includes(back)),
  };
}
